import { useApiQuery } from "../../../core/api/useApiQuery"
import { useApiMutation } from "../../../core/api/useApiMutation"
import { useDateTime } from "../../../modules/localization/useDateTime"
import { fetchOrderChatMessages, sendOrderChatMessage, OrderChatQuery } from "./orderChatApi"
import { ChatChannel, UserType, ChatMessagePayload, ChatMessageSender } from "./orderChatTypes"

function buildChatMessagePayload(body: string, sender?: ChatMessageSender): ChatMessagePayload {
    if (!sender) {
        return { body, channels: [ChatChannel.ORDER_CHAT] }
    }
    if (sender.type === UserType.MERCHANT) {
        return {
            body,
            sender: {
                type: UserType.MERCHANT,
                merchant: { id: sender.merchantId },
                user: { id: sender.userId },
            },
            channels: [ChatChannel.ORDER_CHAT],
        }
    }
    return {
        body,
        sender: { type: UserType.USER, company: { id: sender.companyId }, user: { id: sender.userId } },
        channels: [ChatChannel.ORDER_CHAT],
    }
}

export function useOrderChat(orderId: number, sender?: ChatMessageSender, onMessageSent?: () => Promise<void> | void) {
    const dateTimeFactory = useDateTime()
    const {
        data: messagesData,
        isLoading,
        refetch,
    } = useApiQuery(fetchOrderChatMessages, {
        queryName: OrderChatQuery.MESSAGES,
        dependencies: { orderId },
    })

    const { mutate: sendMessage } = useApiMutation(sendOrderChatMessage, {
        async onSuccess() {
            await refetch()
            await onMessageSent?.()
        },
    })

    return {
        messages:
            messagesData?.messages
                .slice()
                .sort(
                    (a, b) =>
                        dateTimeFactory.fromApi(a.created).toMillis() - dateTimeFactory.fromApi(b.created).toMillis(),
                ) ?? [],
        isLoading,
        sendMessage: (body: string) => sendMessage({ orderId, payload: buildChatMessagePayload(body, sender) }),
    }
}
